import Button from '@atoms/Button/Button';
import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import { IMiniBasketResponse } from '@models/IMiniBasket';
import MiniBasketItem from '@molecules/MiniBasketItem/MiniBasketItem';
import { removeItemFromCart } from '@services/cart';
import useCartSizeStore from '@state/CartSizeStore';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import updateDataLayer from '@utils/helpers/gtm';
import axios from 'axios';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';

interface Props {
  readonly miniBasketEndpoint: string;
  readonly miniBasketTitle: string;
}

const MiniBasket = ({ miniBasketEndpoint, miniBasketTitle }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [basketPayload, setBasketPayload] =
    useState<IMiniBasketResponse | null>(null);
  const { cartSizeName, setCartSize } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  const { setErrorBannerNotification } = useErrorNotificationStore();

  const {
    basketLink,
    checkoutLink,
    cartItems = [],
    emptyMiniBasketLabel,
    giftItemNotification,
    removeItemFromCartEndpoint,
    subtotal,
    subtotalLabel,
    trackingData,
    total,
  } = basketPayload ?? {};
  const hasBasketItems = cartItems.length > 0;
  const basketItemsCount = cartItems.reduce(
    (acc, item) => acc + item.selectedQuantity,
    0
  );
  const isDiscounted = total !== subtotal;

  const handleRemoveItemFromCart = async (code: string, isGift?: boolean) => {
    setIsLoading(true);

    try {
      const basketResponsePayload = await removeItemFromCart(
        removeItemFromCartEndpoint!,
        code,
        isGift
      );

      setBasketPayload({
        ...basketPayload!,
        cartItems: basketResponsePayload.basket.cartItems,
        total: basketResponsePayload.basket.orderSummary.total,
        subtotal: basketResponsePayload.basket.orderSummary.subtotal,
      });
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);

      const currentProduct = (trackingData?.products || []).find(
        (product) => product.item_id === code
      );

      if (currentProduct)
        updateDataLayer({
          event: 'removeFromCart',
          ecommerce: {
            currency: basketResponsePayload.basket.trackingData?.currencyCode,
            items: [{ ...currentProduct, quantity: 0 }],
            value: basketResponsePayload.basket.orderSummary.totalValue,
            quantityChange: -currentProduct.quantity,
          },
        });
    } catch (error) {
      setErrorBannerNotification(ErrorBannerNotificationEnum.CartRemoveItem);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getMiniBasketDetails = async () => {
      try {
        setIsLoading(true);

        const { data } = await axios.get<IMiniBasketResponse>(
          miniBasketEndpoint
        );
        setBasketPayload(data);
      } catch (error) {
        setErrorBannerNotification(
          ErrorBannerNotificationEnum.MiniBasketDetails
        );
      } finally {
        setIsLoading(false);
      }
    };

    getMiniBasketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniBasketEndpoint]);

  return (
    <div
      className={classNames(
        'relative w-[488px] overflow-hidden rounded-[10px] bg-grey-100'
      )}
      data-testid="CT-157-MiniBasket"
    >
      <p
        className="h6 flex w-full items-center p-5"
        data-testid="CT-157-MiniBasket-Title"
      >
        {miniBasketTitle}
        {hasBasketItems && (
          <>
            &nbsp;
            <span
              className="body-two text-grey-450"
              data-testid="CT-157-MiniBasket-Items-Count"
            >
              ({basketItemsCount})
            </span>
          </>
        )}
      </p>

      {isLoading && (
        <div
          className={classNames(
            'absolute top-0 z-100 flex h-full w-full items-center justify-center',
            { 'bg-black-100/10': basketPayload }
          )}
        >
          <LoadingSpinner
            pulseItemClasses="bg-black h-5 w-5"
            data-testid="CT-157-MiniBasket-Loading-Spinner"
          />
        </div>
      )}

      {!basketPayload && <div className="min-h-[245px]" />}

      <AnimatePresence>
        {basketPayload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex max-h-[510px] w-full flex-col"
            data-testid="CT-157-MiniBasket-Container"
          >
            {hasBasketItems ? (
              <>
                <div className="flex flex-shrink flex-col gap-5 overflow-auto px-5 pb-5">
                  {cartItems.map((item, index) => (
                    <MiniBasketItem
                      {...item}
                      giftItemNotification={giftItemNotification}
                      key={item.code}
                      onRemoveItemFromCart={handleRemoveItemFromCart}
                      dataTestId={`CT-157-MiniBasket-Item-${index}`}
                    />
                  ))}
                </div>

                <div
                  className={classNames(
                    'flex w-full items-end justify-between bg-black-100 p-5 dark:bg-white',
                    {
                      'flex-col': isDiscounted,
                    }
                  )}
                  data-testid="CT-157-MiniBasket-Bottom-Bar"
                >
                  <div className="self-start">
                    <p
                      className="body-three text-grey dark:text-black-100"
                      data-testid="CT-157-MiniBasket-Subtotal-Label"
                    >
                      {subtotalLabel}
                    </p>
                    {isDiscounted ? (
                      <p
                        className="body-one body-one--bold text-yellow dark:text-black-100"
                        data-testid="CT-157-MiniBasket-Subtotal-Value"
                      >
                        {total}{' '}
                        <span className="body-two text-grey-200 line-through dark:text-grey-500">
                          {subtotal}
                        </span>
                      </p>
                    ) : (
                      <p
                        className="body-one body-one--bold text-white dark:text-black-100"
                        data-testid="CT-157-MiniBasket-Subtotal-Value"
                      >
                        {total}
                      </p>
                    )}
                  </div>

                  <div
                    className={classNames(
                      'flex flex-wrap gap-4',
                      isDiscounted
                        ? 'w-full justify-between pt-5'
                        : 'justify-end'
                    )}
                  >
                    <Button
                      link={basketLink}
                      secondary
                      isLightMode
                      className={classNames(
                        'text-center text-white dark:text-black',
                        { 'flex-grow': isDiscounted }
                      )}
                      dataTestId="CT-157-MiniBasket-Basket-Link"
                    />
                    <Button
                      link={checkoutLink}
                      className={classNames('text-center', {
                        'flex-grow': isDiscounted,
                      })}
                      dataTestId="CT-157-MiniBasket-Checkout-Link"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex min-h-[245px] w-full items-center justify-center">
                <p
                  className="body-one mb-12 text-grey-450"
                  data-testid="CT-157-MiniBasket-Empty-Basket-Label"
                >
                  {emptyMiniBasketLabel}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MiniBasket;
